import React, { useContext, useEffect, useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    TextField,
    Stack,
    InputLabel,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Checkbox,
    Menu
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Tree from 'rc-tree';
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LightTooltip from '../../../../commonComponents/LightTooltip';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { ParentScreenCtx } from '../../../../pages/workbench_build/WorkbenchBuild';
import { treeActions } from '../../../../redux/slices/treeSlice';
import { scrollToSelectedItem } from '../../../../utils/utilFunctions';
import WorkflowSourceType from '../../../../commonComponents/WorkflowSourceTypeComponent/WorkflowSourceType';
import { definedHeaderFields, messageSelectorOperatorsDropDown } from '../../../../constants/constantList';

const MqSeriesMessageSelector = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [selectedKey, setSelectedKey] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const conditionRef = React.useRef(null);
    const [treeData, setTreeData] = useState([]);
    const [selectedNode, setSelectedNode] = useState({});
    const dispatch = useDispatch();
    const parentScreen = useContext(ParentScreenCtx);
    const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
    const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
    const rightSelectedNode = rightTree.tree[rightTree.currentTab].selectedNode;

    const operatorDropdown = messageSelectorOperatorsDropDown.map((operator, index) => {
        return (<MenuItem key={index} value={operator.value}>{operator.text}</MenuItem>)
    });

    const headerValueDropdown = definedHeaderFields
        .sort((a, b) => t(a.label).localeCompare(t(b.label)))
        .map((field, index) => {
            return (<MenuItem key={index} value={t(field.label)}>{t(field.label)}</MenuItem>)
        });

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const saveToRedux = (key, value, bulk) => {
        const clonedData = cloneDeep(translatedData);
        
        eachDeep(clonedData, (obj) => {
            if (obj?.key === rightSelectedNode?.key) {
                if (key) {
                    if (key === "MESSAGE_SELECTOR_CONDITIONS") {
                        obj[key] = value;
                    } else {
                        obj[key] = value;
                    }
                } else {
                    Object.keys(bulk).forEach((e) => {
                        obj[e] = bulk[e];
                    });
                }
                
                dispatch(
                    treeActions.setRightTreeSelectedNode({
                        screen: parentScreen,
                        type: rightTree.currentTab,
                        data: obj,
                    })
                );
            }
        });
        dispatch(
            treeActions.setRightTreeTransData({
                screen: parentScreen,
                type: rightTree.currentTab,
                data: clonedData,
            })
        );
    };

    const submitHandler = (data) => {
        const getCleanedData = (data, operator) => {
            return data?.map(e => {
                const newObj = {
                    ...e,
                    ...((!e?.hasOwnProperty("CHILDOPERATOR") && operator) ? { OPERATOR: operator } : {}),
                    ...(e?.children?.length ? { CONDITION: getCleanedData(e?.children, e?.CHILDOPERATOR) } : {})
                }
                const keysRemoved = ["key", "title", "children"];

                keysRemoved.forEach(e => {
                    if (newObj?.hasOwnProperty(e) || newObj[e] === null) delete newObj?.[e];
                })

                Object.keys(newObj).forEach((key) => {
                    if (newObj[key] === undefined || newObj[key] === null || key.toLowerCase() === key) delete newObj[key];
                });
                
                return newObj;
            })
        }
        const cleanedData = getCleanedData(data);
        saveToRedux("CONDITION", cleanedData);
    };

    const handleMenuClose = (source) => {
        setAnchorEl(null);
        if (source) {
            const data = JSON.parse(JSON.stringify(treeData));
            const newNode = source === "CONDITION" ? conditionNode : compoundConditionNode;
            if (!selectedKey?.length) {
                data.push(newNode);
                setSelectedKey(["cf-0"]);
                setSelectedNode({ key: "cf-0", ...newNode });
                if (source === "COMPOUND_CONDITION") setExpandedKeys(["cf-0"]);
            } else {
                const keyArray = selectedKey?.[0]?.split("-") || [];
                const [pre, ...rest] = keyArray;
                let tempData = data;
                for (let i = 0; i < rest.length; i++) {
                    const index = Number(rest[i]);
                    if (i === rest.length - 1) {
                        if (tempData?.[index]?.CHILDOPERATOR && source === "CONDITION") {
                            tempData = tempData?.[index]?.CONDITION;
                            tempData.push(newNode);
                            const key = selectedKey[0] + "-" + `${tempData?.length - 1}`;
                            setSelectedKey([key]);
                            setSelectedNode({ key, ...newNode });
                            setExpandedKeys((prev) => [...prev, selectedKey[0]]);
                        } else {
                            tempData.push(newNode);
                            keyArray.pop();
                            keyArray.push(tempData.length - 1);
                            setSelectedKey([keyArray?.join("-")]);
                            setSelectedNode({ key: keyArray?.join("-"), ...newNode });
                            if (source === "COMPOUND_CONDITION")
                                setExpandedKeys((prev) => [...prev, keyArray?.join("-")]);
                        }
                    } else {
                        tempData = tempData?.[index]?.CONDITION;
                    }
                }
            }
            submitHandler(data);
            dataToRCTree(data);
        }
    };

    const dataToRCTree = (data, initialRender) => {
        const clonedData = JSON.parse(JSON.stringify(data));
        const loopData = (data, parentKey) => {
            return data?.map((e, i) => {
                if (e.CONDITION) {
                    e.ICON_DISPLAY = "COMPOUND_CONDITION";
                } else {
                    e.ICON_DISPLAY = "MAPPING_CONDITION";
                }
                e["key"] = parentKey + "-" + i;
                if (e?.CONDITION && e?.CHILDOPERATOR)
                    e.title = e?.NAME + ` (${e.CHILDOPERATOR})`;
                else e.title = e?.NAME;
                if (e?.CONDITION?.length)
                    e.children = loopData(e?.CONDITION, parentKey + "-" + i);
                else e.children = [];
                return e;
            });
        };

        const newData = loopData(clonedData, "cf");
        if (initialRender) {
            setSelectedKey(["cf-0"]);
            setSelectedNode(newData?.[0] || {});
        }
        setTreeData(newData);
    };

    const conditionsUpdater = (key, value, valuesObj) => {
        const rcTreeData = cloneDeep(treeData);
        eachDeep(rcTreeData, (obj) => {
            if (obj?.key && obj?.key === selectedNode?.key) {
                if (!key) {
                    Object.keys(valuesObj).forEach((e) => {
                        obj[e] = valuesObj[e];
                    });
                } else obj[key] = value;
                setSelectedNode(obj);
            }
        });
        submitHandler(rcTreeData);
        dataToRCTree(rcTreeData);
    };

    const conditionDeleteHandler = () => {
        const data = JSON.parse(JSON.stringify(treeData));
        const keyArray = selectedKey?.[0]?.split("-") || [];
        const [pre, ...rest] = keyArray;
        let tempData = data;
        let parentNode = {};
        for (let i = 0; i < rest.length; i++) {
            const index = Number(rest[i]);
            if (i === rest.length - 1) {
                let key = selectedKey?.[0]?.split("-");
                if (index === 0) {
                    if (key?.length === 2 && tempData?.length === 1) {
                        setSelectedKey([]);
                        setSelectedNode({});
                    }
                    if (key?.length === 2 && tempData?.length > 1) {
                        setSelectedKey(["cf-0"]);
                        setSelectedNode(tempData?.[1] || {});
                    } else {
                        if (key.length > 2) {
                            key.pop();
                            setSelectedKey([key.join("-")]);
                            setSelectedNode(parentNode);
                        } else {
                            setSelectedKey([]);
                            setSelectedNode({});
                        }
                    }
                } else {
                    key?.pop();
                    key?.push(index - 1);
                    setSelectedKey([key?.join("-")]);
                    setSelectedNode(tempData?.[index - 1] || {});
                }
                tempData.splice(index, 1);
            } else {
                parentNode = tempData?.[index];
                tempData = tempData?.[index]?.CONDITION;
            }
        }
        submitHandler(data);
        dataToRCTree(data);
    };

    const titleRender = (node) => {
        return <span data-key={`${node?.key}`}>{node.title}</span>;
    };

    const Icon = (node) => {
        return (
            <span
                data-key={`${node?.key}`}
                className={"tree-icon " + node.ICON_DISPLAY}
            ></span>
        );
    };

    const switcherIcon = (obj) => {
        if (obj.isLeaf) return;
        if (obj.expanded) return <ArrowDropDownIcon />;
        else return <ArrowRightIcon sx={{ color: "#666666" }} />;
    };

    const selectHandler = (selectedKeys, info) => {
        setSelectedKey([info.node.key]);
        setSelectedNode(info.node);
    };

    const expandHandler = (expandedKeys, { expanded, node }) => {
        setExpandedKeys(expandedKeys);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        conditionsUpdater(name, type === "checkbox" ? checked ? "TRUE" : "FALSE" : value);
    };

    useEffect(() => {
        if (conditionRef?.current) {
            scrollToSelectedItem(selectedKey, conditionRef);
        }
    }, [selectedKey]);

    useEffect(() => {
        // Initialize with existing message selector conditions or empty array
        const existingConditions = rightSelectedNode?.CONDITION || [];
        dataToRCTree(existingConditions, true);
    }, [rightSelectedNode?.key]);

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 400,
                    maxWidth: '90vw'
                }
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with close icon */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderBottom: '1px solid #e0e0e0'
                    }}
                >
                    <Typography variant="h6" component="h2">
                        {t('LBL_MESSAGE_SELECTOR')}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ ml: 2 }}
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* Content area */}
                <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                    <div>
                        <div
                            style={{
                                marginBottom: "4px",
                                overflowX: "hidden",
                                position: "relative",
                            }}
                            ref={conditionRef}
                            className="div-textBox-conditional-field"
                        >
                            <div
                                style={{
                                    backgroundColor: "#F2F2F2",
                                    position: "sticky",
                                    top: "0",
                                    zIndex: "1000",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "93%",
                                        margin: "auto",
                                        padding: "2px",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#666666",
                                            fontSize: "13px",
                                            fontWeight: "500",
                                        }}
                                    >
                                        {t("LBL_CONDITIONS")}
                                    </div>
                                    <div style={{ display: "flex", gap: "5px" }}>
                                        <div>
                                            <LightTooltip arrow onClick={handleMenuClick}>
                                                <span
                                                    style={{ cursor: "pointer" }}
                                                    className="ADD_MUI"
                                                ></span>
                                            </LightTooltip>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={() => handleMenuClose()}
                                                anchorOrigin={{
                                                    vertical: "bottom",
                                                    horizontal: "left",
                                                }}
                                            >
                                                <MenuItem onClick={() => handleMenuClose("CONDITION")}>
                                                    {t("MNU_NEW_COND")}
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => handleMenuClose("COMPOUND_CONDITION")}
                                                >
                                                    {t("MNU_NEW_COMP_COND")}
                                                </MenuItem>
                                            </Menu>
                                        </div>
                                        <LightTooltip
                                            onClick={conditionDeleteHandler}
                                            title={t("DELETE")}
                                            arrow
                                        >
                                            <span
                                                style={{ cursor: "pointer" }}
                                                className="DELETE_MUI"
                                            ></span>
                                        </LightTooltip>
                                    </div>
                                </div>
                            </div>
                            <Tree
                                titleRender={titleRender}
                                treeData={treeData}
                                icon={Icon}
                                selectedKeys={selectedKey}
                                switcherIcon={switcherIcon}
                                onSelect={selectHandler}
                                expandedKeys={expandedKeys}
                                onExpand={expandHandler}
                            />
                        </div>
                    </div>

                    <Stack spacing={2} px={1} mt={2}>
                        <TextField
                            disabled={!selectedNode?.hasOwnProperty("key")}
                            fullWidth
                            size="small"
                            label={t("LBL_CONDITION_NAME")}
                            name="NAME"
                            value={selectedNode.NAME || ""}
                            onChange={(e) => {
                                const name = e?.target?.value ? e?.target?.value : "UNNAMED";
                                conditionsUpdater("NAME", name);
                            }}
                        />
                        {selectedNode?.hasOwnProperty("CHILDOPERATOR") ? (
                            <>
                                <InputLabel>{t("LBL_SEL_OPER_FOR_COND_CONTAINED_WITHIN_COMP_COND")}</InputLabel>
                                <FormControl
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                    ml={3}
                                    disabled={!selectedNode?.hasOwnProperty("key")}
                                >
                                    <FormLabel>{t("LBL_COMPOUND_OPER")}</FormLabel>
                                    <RadioGroup
                                        row
                                        name="CHILDOPERATOR"
                                        value={selectedNode?.CHILDOPERATOR || "AND"}
                                        onChange={(e) => {
                                            conditionsUpdater("CHILDOPERATOR", e.target.value);
                                        }}
                                    >
                                        <FormControlLabel
                                            value={"OR"}
                                            control={<Radio />}
                                            label={"OR"}
                                        />
                                        <FormControlLabel
                                            value={"AND"}
                                            control={<Radio />}
                                            label={"AND"}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </>
                        ) : (
                            <>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t("LBL_CUSTOM_HEADER_VALUE")}</InputLabel>
                                    <Select
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                },
                                            },
                                        }}
                                        size="small"
                                        name="MAPNAME"
                                        value={selectedNode?.MAPNAME || ""}
                                        disabled={!selectedNode?.hasOwnProperty("key")}
                                        onChange={handleChange}
                                    >
                                        {headerValueDropdown}
                                    </Select>
                                </FormControl>
                                <InputLabel>{t("LBL_OPERATOR")}</InputLabel>
                                <div style={{ display: "flex" }}>
                                    <FormControlLabel
                                        disabled={!selectedNode?.hasOwnProperty("key")}
                                        control={
                                            <Checkbox
                                                name="COMPARE_ACTION_NOT"
                                                checked={selectedNode.COMPARE_ACTION_NOT === "TRUE"}
                                                onChange={handleChange}
                                            />
                                        }
                                        label={t("BTN_NOT")}
                                    />
                                    <FormControl disabled={!selectedNode?.hasOwnProperty("key")} fullWidth size="small">
                                        <InputLabel>{t("LBL_OPERATOR")}</InputLabel>
                                        <Select
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 200,
                                                    },
                                                },
                                            }}
                                            size="small"
                                            name="COMPARE"
                                            value={selectedNode?.COMPARE || "SELECT"}
                                            onChange={handleChange}
                                        >
                                            {operatorDropdown}
                                        </Select>
                                    </FormControl>
                                </div>
                                <div style={{ display: "flex" }}>
                                    <FormControlLabel
                                        disabled={!selectedNode?.hasOwnProperty("key")}
                                        control={
                                            <Checkbox
                                                name="COMPARE_ACTION_IGNORECASE"
                                                checked={selectedNode.COMPARE_ACTION_IGNORECASE === "TRUE"}
                                                onChange={handleChange}
                                            />
                                        }
                                        label={t("IGNORECASE")}
                                    />
                                    <FormControlLabel
                                        disabled={!selectedNode?.hasOwnProperty("key")}
                                        control={
                                            <Checkbox
                                                name="COMPARE_ACTION_TRIM"
                                                checked={selectedNode.COMPARE_ACTION_TRIM === "TRUE"}
                                                onChange={handleChange}
                                            />
                                        }
                                        label={t("LBL_TRIM_DATA")}
                                    />
                                </div>

                                {!["ISNULL", "IS_NUMERIC", "IS_DATE", "TRUE", "FALSE"].includes(selectedNode.COMPARE) ? (
                                    <>
                                        <Divider />
                                        <WorkflowSourceType
                                            disabled={!selectedNode?.hasOwnProperty("key")}
                                            node={{
                                                key: selectedNode?.key,
                                                RECORD_TYPE: selectedNode?.RECORD_TYPE2,
                                                WFITEM: selectedNode?.WF_ITEM_2,
                                                OUTBOUND_NAME: selectedNode?.OUTBOUND_2,
                                                MAPNAME: selectedNode?.MAPNAME2,
                                                VALUE: selectedNode?.VALUE,
                                                WF_ITEM_ID: selectedNode?.WF_ITEM_ID_2,
                                            }}
                                            valuesHandler={(values) => {
                                                conditionsUpdater(null, null, {
                                                    RECORD_TYPE2: values?.RECORD_TYPE,
                                                    WF_ITEM_2: values?.WFITEM,
                                                    OUTBOUND_2: values?.OUTBOUND_NAME,
                                                    MAPNAME2: values?.MAPNAME,
                                                    WF_ITEM_ID_2: values?.WF_ITEM_ID,
                                                    VALUE: values?.VALUE,
                                                });
                                            }}
                                            addConstantControl={true}
                                            hideSourceTypeOptions={["CALL_WORKFLOW_DATA",
                                                "ELEM_VAL_ERR",
                                                "FNCT_GRP_VAL_ERR",
                                                "PLUGIN",
                                                "PROCESSING_ERRORS",
                                                "SEG_VAL_ERR",
                                                "STATUS_MESSAGES",
                                                "TRN_SET_VAL_ERR",
                                                "TRANSLATION_ERRORS",
                                                "ERR_VALIDATION"]}
                                        />
                                    </>
                                ) : null}
                                
                                <FormControlLabel
                                    disabled={!selectedNode?.hasOwnProperty("key")}
                                    control={
                                        <Checkbox
                                            name="MAP2_HEX"
                                            checked={selectedNode.MAP2_HEX === "TRUE"}
                                            onChange={handleChange}
                                            sx={{ paddingLeft: '0 !important' }}
                                        />
                                    }
                                    label={t("BTN_IS_HEX")}
                                />
                            </>
                        )}
                    </Stack>
                </Box>

                {/* Footer with Done button */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={onClose}
                        size="small"
                    >
                        {t('BTN_DONE')}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

// Condition node constants
const conditionNode = {
    "NAME": "UNNAMED",
    "COMPARE": "",
    "RECORD_TYPE": "SESSION_DATA",
    "RECORD_TYPE2": "SESSION_DATA",
    "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
    "COMPARE_ACTION_TRIM": "FALSE",
    "COMPARE_ACTION_IGNORECASE": "FALSE",
    "COMPARE_ACTION_NOT": "FALSE",
    "BIT_MAP": "",
};

const compoundConditionNode = {
    "NAME": "COMPOUND CONDITION",
    "CHILDOPERATOR": "AND",
    "CONDITION": [
        {
            "NAME": "UNNAMED1",
            "OPERATOR": "AND",
            "COMPARE": "",
            "RECORD_TYPE": "SESSION_DATA",
            "RECORD_TYPE2": "SESSION_DATA",
            "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
            "COMPARE_ACTION_TRIM": "FALSE",
            "COMPARE_ACTION_IGNORECASE": "FALSE",
            "COMPARE_ACTION_NOT": "FALSE",
            "BIT_MAP": "",
        },
        {
            "NAME": "UNNAMED2",
            "OPERATOR": "AND",
            "COMPARE": "",
            "RECORD_TYPE": "SESSION_DATA",
            "RECORD_TYPE2": "SESSION_DATA",
            "WF_ITEM_DEPENDENCY_TIMEOUT": "5000",
            "COMPARE_ACTION_TRIM": "FALSE",
            "COMPARE_ACTION_IGNORECASE": "FALSE",
            "COMPARE_ACTION_NOT": "FALSE",
            "BIT_MAP": "",
        }
    ]
};

export default MqSeriesMessageSelector; 